import { Pipe, PipeTransform } from '@angular/core';
import { ReplaceMap } from 'src/lib/fliko/interfaces/replaceMap';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { Collection } from 'src/lib/fliko/types/database.type';
import { tTime } from 'src/lib/fliko/types/time.type';
import { StrValue } from 'src/lib/fliko/types/typeish.type';
import { Localised } from 'src/lib/fliko/models/translation/generic';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { DateFormat, LocalizationHelper, NumberFormat } from '../helpers/localization.helper';
import { LocLabel, LocLabelModifier, parseLocLabelArgs } from '../components/clickable-row-table/models/data-table-config.models';
import { LocalizationService } from '../services/localization.service';

type LLMod = StrValue<LocLabelModifier>;

// #region Basic
@Pipe({ name: 'loc', pure: false })
export class LocalizationPipe implements PipeTransform {
  constructor(private ls: LocalizationService) {}

  transform(key: string | null | undefined): string {
    return key ? this.ls.translate(key) : '';
  }
}

@Pipe({ name: 'locRep', pure: false })
export class LocalizeReplacePipe implements PipeTransform {
  constructor(private ls: LocalizationService) {}

  transform(key: string, obj: Collection<string | number>): string {
    return this.ls.trReplace(key, obj);
  }
}
// #endregion

// #region Output with Alterations
@Pipe({ name: 'locCap', pure: false })
export class LocalizeCapitalizePipe implements PipeTransform {
  constructor(private lp: LocalizationPipe) {}

  transform(key: string | null | undefined): string {
    return StrHlp.capitalize(this.lp.transform(key));
  }
}

@Pipe({ name: 'locUpper', pure: false })
export class LocalizeUpperCasePipe implements PipeTransform {
  constructor(private lp: LocalizationPipe) {}

  transform(key: string | null | undefined): string {
    return this.lp.transform(key).toString().toLocaleUpperCase();
  }
}

@Pipe({ name: 'locLower', pure: false })
export class LocalizeLowerCasePipe implements PipeTransform {
  constructor(private lp: LocalizationPipe) {}

  transform(key: string | null | undefined): string {
    return this.lp.transform(key).toString().toLocaleLowerCase();
  }
}

@Pipe({ name: 'locPlu', pure: false })
export class LocalizePluralPipe implements PipeTransform {
  constructor(private lp: LocalizationPipe) {}

  // TODO: handle changed empty parameter
  transform(key: string, amount: number, empty?: string): string {
    return this.lp.transform(GrammarHelper.getCounterKey(key, amount, empty ?? ''));
  }
}
// #endregion

// #region Date

@Pipe({ name: 'lDate', pure: false })
export class LocalizeDateTimePipe implements PipeTransform {
  private readonly locHelper = new LocalizationHelper(this.ls);
  constructor(private ls: LocalizationService) {}

  transform(value: tTime, format: DateFormat, empty?: string, tz?: string): string {
    return this.locHelper.localizeDate(value, format, empty, tz);
  }
}

@Pipe({ name: 'lTime', pure: false })
export class TimeValuePipe implements PipeTransform {
  private readonly locHelper = new LocalizationHelper(this.ls);
  constructor(private ls: LocalizationService) {}

  transform(value: number, format: string = 'dhm'): string {
    return this.locHelper.getTimeHR(value, format);
  }
}

@Pipe({ name: 'lTimeR', pure: false })
export class RelativeTimePipe implements PipeTransform {
  private readonly locHelper = new LocalizationHelper(this.ls);
  constructor(private ls: LocalizationService) {}

  transform(value: tTime): string {
    return this.locHelper.relativeTime(value);
  }
}

@Pipe({ name: 'lDaysR', pure: false })
export class RelativeDaysPipe implements PipeTransform {
  private readonly locHelper = new LocalizationHelper(this.ls);
  constructor(private ls: LocalizationService) {}

  transform(value: number): string {
    return this.locHelper.relativeDays(value);
  }
}

// #endregion
@Pipe({ name: 'lNumber', pure: false })
export class LocalizeNumberPipe implements PipeTransform {
  private readonly locHelper = new LocalizationHelper(this.ls);
  constructor(private ls: LocalizationService) {}

  transform(value: number | string | undefined | null, format: NumberFormat): string {
    return this.locHelper.formatNumber(value, format);
  }
}

@Pipe({ name: 'locLabel', pure: false })
export class LocalizeLabelPipe implements PipeTransform {
  private readonly locHelper = new LocalizationHelper(this.ls);
  constructor(private ls: LocalizationService) {}
  transform(value: LocLabel | Localised<string> | string | undefined | null) {
    if (typeof value === 'string') return this.ls.translate(value);
    else if (value && 'key' in value) return this.locHelper.locLabel(value);
    else if (value) return this.ls.getData(value) ?? '';
    else return '';
  }
}

// #region If Else

function ifElseKey(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string) {
  return `${prefix}.${value ? trueSuffix : falseSuffix}`;
}

@Pipe({ name: 'keyIfElse' })
export class KeyIfElsePipe implements PipeTransform {
  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string): string {
    return ifElseKey(value, prefix, trueSuffix, falseSuffix);
  }
}

@Pipe({ name: 'llIfElse' })
export class LocLabelIfElsePipe implements PipeTransform {
  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string, modifier: LLMod): LocLabel;
  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string, replaceMap: ReplaceMap): LocLabel;
  // eslint-disable-next-line max-len
  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string, modifier: LLMod, replaceMap: ReplaceMap): LocLabel; // prettier-ignore
  // implementation
  transform(
    value: boolean,
    prefix: string,
    trueSuffix: string,
    falseSuffix: string,
    llArg1: LLMod | ReplaceMap,
    llArg2?: ReplaceMap
  ): LocLabel {
    const key = ifElseKey(value, prefix, trueSuffix, falseSuffix);
    return { key, ...parseLocLabelArgs(llArg1, llArg2) };
  }
}

@Pipe({ name: 'locIfElse', pure: false })
export class LocalizeIfElsePipe implements PipeTransform {
  constructor(private ls: LocalizationService) {}

  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string): string;
  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string, modifier: LLMod): string;
  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string, replaceMap: ReplaceMap): string;
  // eslint-disable-next-line max-len
  transform(value: boolean, prefix: string, trueSuffix: string, falseSuffix: string, modifier: LLMod, replaceMap: ReplaceMap): string; // prettier-ignore
  // Implementation
  transform(
    value: boolean,
    prefix: string,
    trueSuffix: string,
    falseSuffix: string,
    llArg1?: ReplaceMap | LLMod,
    llArg2?: ReplaceMap
  ): string {
    const key = ifElseKey(value, prefix, trueSuffix, falseSuffix);
    const lh = new LocalizationHelper(this.ls);
    return lh.locLabel({ key, ...parseLocLabelArgs(llArg1, llArg2) });
  }
}

// #endregion
